import { useRouteError, isRouteErrorResponse, useNavigate } from 'react-router-dom';
import { Button, Group, Stack, Text, Card, Code } from '@mantine/core';
import { WarningCircle, ArrowLeft, ArrowClockwise, CaretDown, CaretUp } from '@phosphor-icons/react';
import { useState } from 'react';

export function RouteErrorBoundary() {
  const error = useRouteError();
  const navigate = useNavigate();
  const [detailsOpen, setDetailsOpen] = useState(false);

  let errorMessage = 'An unexpected error occurred while loading this page.';
  let errorDetails = '';

  if (isRouteErrorResponse(error)) {
    errorMessage = `${error.status} ${error.statusText || 'Error'}`;
    errorDetails = error.data?.message || (typeof error.data === 'string' ? error.data : JSON.stringify(error.data, null, 2));
  } else if (error instanceof Error) {
    errorMessage = error.message;
    errorDetails = error.stack || '';
  } else if (typeof error === 'string') {
    errorMessage = error;
  }

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '60vh',
        padding: 24,
      }}
    >
      <Card
        style={{
          maxWidth: 600,
          width: '100%',
          background: 'var(--surface)',
          border: '1px solid var(--boundary)',
          borderRadius: 8,
          padding: 28,
          boxShadow: 'var(--card-shadow)',
        }}
      >
        <Stack gap={16} align="center" ta="center">
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              background: 'var(--danger-dim)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid var(--danger-border)',
            }}
          >
            <WarningCircle size={32} color="var(--danger)" weight="duotone" />
          </div>

          <div>
            <Text size="lg" fw={700} c="var(--text-primary)" mb={4}>
              Page Error
            </Text>
            <Text size="sm" c="var(--text-secondary)">
              {errorMessage}
            </Text>
          </div>

          <Group gap={12} mt={8}>
            <Button
              variant="outline"
              size="sm"
              leftSection={<ArrowLeft size={14} />}
              onClick={() => navigate(-1)}
              style={{
                borderColor: 'var(--boundary)',
                color: 'var(--text-primary)',
              }}
            >
              Go Back
            </Button>
            <Button
              size="sm"
              leftSection={<ArrowClockwise size={14} />}
              onClick={() => window.location.reload()}
              style={{
                background: 'var(--amber)',
                color: '#FFFFFF',
              }}
            >
              Reload Page
            </Button>
          </Group>

          {errorDetails && (
            <div style={{ width: '100%', marginTop: 8 }}>
              <Button
                variant="subtle"
                size="xs"
                onClick={() => setDetailsOpen((v) => !v)}
                rightSection={detailsOpen ? <CaretUp size={12} /> : <CaretDown size={12} />}
                style={{ color: 'var(--text-disabled)', fontSize: 12 }}
              >
                {detailsOpen ? 'Hide technical details' : 'Show technical details'}
              </Button>
              {detailsOpen && (
                <Code
                  block
                  style={{
                    marginTop: 8,
                    maxHeight: 180,
                    overflowY: 'auto',
                    textAlign: 'left',
                    fontSize: 11,
                    fontFamily: 'Geist Mono, monospace',
                    background: 'var(--bg-root)',
                    border: '1px solid var(--boundary)',
                    color: 'var(--text-secondary)',
                    borderRadius: 4,
                    padding: 10,
                  }}
                >
                  {errorDetails}
                </Code>
              )}
            </div>
          )}
        </Stack>
      </Card>
    </div>
  );
}
